import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, UsersIcon, PencilIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import PaginationFooter from '@/components/PaginationFooter';
import { useI18n } from '@/hooks/useI18n';

interface EmployeeGroup {
    id: string;
    name: string;
    description: string | null;
    is_active: boolean;
    manager_count: number;
    member_count: number;
    created_at: string;
    updated_at: string | null;
}

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    is_active: boolean;
}

interface User {
    id: string;
    email: string;
    is_active: boolean;
}

interface GroupFormData {
    name: string;
    description: string;
    is_active: boolean;
    manager_ids: string[];
    employee_ids: string[];
}

export default function EmployeeGroupsPage() {
    const { t } = useI18n();
    const [groups, setGroups] = useState<EmployeeGroup[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<EmployeeGroup | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState<GroupFormData>({
        name: '',
        description: '',
        is_active: true,
        manager_ids: [],
        employee_ids: []
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 0
    });

    useEffect(() => {
        fetchGroups();
        fetchEmployees();
        fetchUsers();
    }, [searchTerm, pagination.page, pagination.limit]);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const skip = (pagination.page - 1) * pagination.limit;
            const params = new URLSearchParams({
                skip: skip.toString(),
                limit: pagination.limit.toString(),
                ...(searchTerm && { search: searchTerm })
            });

            const response = await apiFetch(`/api/people/groups/?${params}`);
            if (response.ok) {
                const data = await response.json();
                setGroups(data.groups);
                setPagination(prev => ({
                    ...prev,
                    total: data.total,
                    pages: data.pagination.pages
                }));
            } else {
                throw new Error(t('groups.errors.fetchFailed', 'people'));
            }
        } catch (err) {
            console.error('Error fetching groups:', err);
            setError(err instanceof Error ? err.message : t('groups.errors.fetchFailed', 'people'));
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await apiFetch('/api/people/employees/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setEmployees(data.employees || []);
            }
        } catch (err) {
            console.error('Error fetching employees:', err);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await apiFetch('/api/users/');
            if (response.ok) {
                const data = await response.json();
                setUsers(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            is_active: true,
            manager_ids: [],
            employee_ids: []
        });
    };

    const openCreateModal = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const openEditModal = async (group: EmployeeGroup) => {
        setSelectedGroup(group);
        setLoading(true);
        try {
            // Fetch full group details including managers and members
            const response = await apiFetch(`/api/people/groups/${group.id}`);
            if (response.ok) {
                const groupDetails = await response.json();
                setFormData({
                    name: groupDetails.name,
                    description: groupDetails.description || '',
                    is_active: groupDetails.is_active,
                    manager_ids: groupDetails.managers?.map((m: any) => m.id) || [],
                    employee_ids: groupDetails.members?.map((m: any) => m.id) || []
                });
                setShowEditModal(true);
            } else {
                throw new Error(t('groups.errors.fetchDetailsFailed', 'people'));
            }
        } catch (err) {
            console.error('Error fetching group details:', err);
            setError(err instanceof Error ? err.message : t('groups.errors.fetchDetailsFailed', 'people'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            setError(t('groups.errors.nameRequired', 'people'));
            return;
        }
        if (formData.manager_ids.length === 0) {
            setError(t('groups.errors.managerRequired', 'people'));
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const response = await apiFetch('/api/people/groups/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                await fetchGroups();
                setShowCreateModal(false);
                resetForm();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || t('groups.errors.createFailed', 'people'));
            }
        } catch (err) {
            console.error('Error creating group:', err);
            setError(err instanceof Error ? err.message : t('groups.errors.createFailed', 'people'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedGroup) return;

        if (formData.manager_ids.length === 0) {
            setError(t('groups.errors.managerRequired', 'people'));
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            // Update basic group info
            const updateData = {
                name: formData.name,
                description: formData.description || null,
                is_active: formData.is_active
            };

            const response = await apiFetch(`/api/people/groups/${selectedGroup.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || t('groups.errors.updateFailed', 'people'));
            }

            // Update managers
            const managersResponse = await apiFetch(`/api/people/groups/${selectedGroup.id}/managers`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_ids: formData.manager_ids })
            });

            if (!managersResponse.ok) {
                const errorData = await managersResponse.json();
                throw new Error(errorData.detail || t('groups.errors.updateManagersFailed', 'people'));
            }

            // Update members
            const membersResponse = await apiFetch(`/api/people/groups/${selectedGroup.id}/members`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ employee_ids: formData.employee_ids })
            });

            if (!membersResponse.ok) {
                const errorData = await membersResponse.json();
                throw new Error(errorData.detail || t('groups.errors.updateMembersFailed', 'people'));
            }

            await fetchGroups();
            setShowEditModal(false);
            setSelectedGroup(null);
            resetForm();
        } catch (err) {
            console.error('Error updating group:', err);
            setError(err instanceof Error ? err.message : t('groups.errors.updateFailed', 'people'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (groupId: string) => {
        if (!confirm(t('groups.confirmDeactivate', 'people'))) return;

        try {
            const response = await apiFetch(`/api/people/groups/${groupId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                await fetchGroups();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || t('groups.errors.deleteFailed', 'people'));
            }
        } catch (err) {
            console.error('Error deleting group:', err);
            setError(err instanceof Error ? err.message : t('groups.errors.deleteFailed', 'people'));
        }
    };

    const toggleManagerSelection = (userId: string) => {
        setFormData(prev => ({
            ...prev,
            manager_ids: prev.manager_ids.includes(userId)
                ? prev.manager_ids.filter(id => id !== userId)
                : [...prev.manager_ids, userId]
        }));
    };

    const toggleEmployeeSelection = (employeeId: string) => {
        setFormData(prev => ({
            ...prev,
            employee_ids: prev.employee_ids.includes(employeeId)
                ? prev.employee_ids.filter(id => id !== employeeId)
                : [...prev.employee_ids, employeeId]
        }));
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <UserGroupIcon className="h-8 w-8" />
                            {t('groups.groups', 'people')}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {t('groups.subtitle', 'people')}
                        </p>
                    </div>
                    <Button onClick={openCreateModal}>
                        <PlusIcon className="w-4 h-4 mr-2" />
                        {t('actions.addNew', 'common')} {t('groups.title', 'people')}
                    </Button>
                </div>

                {/* Search */}
                <div className="flex gap-4">
                    <Input
                        placeholder={t('common.search', 'people')}
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="max-w-sm"
                    />
                </div>

                {/* Error Display */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                {/* Groups Table */}
                <div className="border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('forms.name', 'common')}</TableHead>
                                <TableHead>{t('forms.description', 'common')}</TableHead>
                                <TableHead className="text-center">{t('groups.managers', 'people')}</TableHead>
                                <TableHead className="text-center">{t('groups.members', 'people')}</TableHead>
                                <TableHead className="text-center">{t('common.status', 'people')}</TableHead>
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8">
                                        {t('common.loading', 'common')}
                                    </TableCell>
                                </TableRow>
                            ) : groups.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8">
                                        <div className="flex flex-col items-center gap-2">
                                            <UsersIcon className="h-12 w-12 text-gray-400" />
                                            <p className="text-muted-foreground">{t('groups.noGroupsFound', 'people')}</p>
                                            <Button variant="outline" onClick={openCreateModal}>
                                                {t('groups.createFirstGroup', 'people')}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                groups.map((group) => (
                                    <TableRow key={group.id} className={!group.is_active ? 'opacity-60 bg-muted/20' : ''}>
                                        <TableCell className="font-medium">{group.name}</TableCell>
                                        <TableCell className="max-w-md truncate">{group.description || '-'}</TableCell>
                                        <TableCell className="text-center">{group.manager_count}</TableCell>
                                        <TableCell className="text-center">{group.member_count}</TableCell>
                                        <TableCell className="text-center">
                                            <Badge variant={group.is_active ? 'default' : 'secondary'}>
                                                {group.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex gap-2 justify-end">
                                                <Button size="sm" variant="outline" onClick={() => openEditModal(group)}>
                                                    <PencilIcon className="w-4 h-4" />
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => handleDelete(group.id)}>
                                                    <TrashIcon className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                <PaginationFooter
                    pagination={pagination}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                    onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }))}
                />
            </div>

            {/* Create Modal */}
            <Dialog open={showCreateModal} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateModal(false);
                    resetForm();
                    setError(null);
                }
            }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{t('groups.createGroupTitle', 'people')}</DialogTitle>
                    </DialogHeader>
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}
                    <form onSubmit={handleCreate}>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="name">{t('groups.groupNameRequired', 'people')}</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="description">{t('forms.description', 'common')}</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label>{t('groups.managersRequired', 'people')}</Label>
                                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                                    {users.filter(u => u.is_active).map(user => (
                                        <label key={user.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50  rounded">
                                            <input
                                                type="checkbox"
                                                checked={formData.manager_ids.includes(user.id)}
                                                onChange={() => toggleManagerSelection(user.id)}
                                                className="rounded"
                                            />
                                            <span>{user.email}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label>{t('groups.membersOptional', 'people')}</Label>
                                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                                    {employees.filter(e => e.is_active).map(employee => (
                                        <label key={employee.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded">
                                            <input
                                                type="checkbox"
                                                checked={formData.employee_ids.includes(employee.id)}
                                                onChange={() => toggleEmployeeSelection(employee.id)}
                                                className="rounded"
                                            />
                                            <span>{employee.first_name} {employee.last_name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting ? t('groups.creating', 'people') : t('groups.createGroup', 'people')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={(open) => {
                if (!open) {
                    setShowEditModal(false);
                    setSelectedGroup(null);
                    resetForm();
                    setError(null);
                }
            }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{t('groups.editGroupTitle', 'people')}</DialogTitle>
                    </DialogHeader>
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}
                    <form onSubmit={handleUpdate}>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="edit-name">{t('groups.groupNameRequired', 'people')}</Label>
                                <Input
                                    id="edit-name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit-description">{t('forms.description', 'common')}</Label>
                                <Textarea
                                    id="edit-description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit-status">{t('common.status', 'people')}</Label>
                                <Select
                                    value={formData.is_active ? 'active' : 'inactive'}
                                    onValueChange={(value) => setFormData({ ...formData, is_active: value === 'active' })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">{t('status.active', 'common')}</SelectItem>
                                        <SelectItem value="inactive">{t('status.inactive', 'common')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>{t('groups.managersRequired', 'people')}</Label>
                                <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-2">
                                    {users.filter(u => u.is_active).map(user => (
                                        <label key={user.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded">
                                            <input
                                                type="checkbox"
                                                checked={formData.manager_ids.includes(user.id)}
                                                onChange={() => toggleManagerSelection(user.id)}
                                                className="rounded"
                                            />
                                            <span>{user.email}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label>{t('groups.membersOptional', 'people')}</Label>
                                <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-2">
                                    {employees.filter(e => e.is_active).map(employee => (
                                        <label key={employee.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded">
                                            <input
                                                type="checkbox"
                                                checked={formData.employee_ids.includes(employee.id)}
                                                onChange={() => toggleEmployeeSelection(employee.id)}
                                                className="rounded"
                                            />
                                            <span>{employee.first_name} {employee.last_name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting ? t('groups.updating', 'people') : t('groups.updateGroup', 'people')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

export const Route = createFileRoute('/people/groups')({
  component: EmployeeGroupsPage,
})
